import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "./store";
import { UserService } from "../services/UserService";
import { UserAddInfo } from "../pages/admin/admin-list-user/adminListUser";
import { DepositsService } from "../services/DepositService";
import { CreateUser, SendDocumentDto, UserDocumentStatus } from "../models/User";
import { WalletService } from "../services/WalletService";
import { setLoggedStatus, setLoggedType } from "./AuthSlice";
import { normalizeRole } from "../auth/permissions";
import { getOrgId } from "./orgHelpers";
import { saveSession } from "../utils/session";
import { openToast } from "./ToastSlice";

export enum UserStatusEnum {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    PROGRESS = 'PROGRESS'
}

export interface OrganizationInfo {
    createdAt : string;
    id : number;
    isActive :  boolean;
    organizationName : string;
    updatedAt : string;
}

export interface organizationUsers {
    fee : string;
    id: number;
    joinedAt: string;
    organization: OrganizationInfo;
}

export interface DocumentInfo {
    document: string,
    status: UserDocumentStatus,
    updatedAt: string
}

export interface UserInfo {
    id: number;
    accessToken: string,
    role: string;
    organizationUsers: organizationUsers[];
    login: string;
    name: string;
    email: string;
    document: DocumentInfo
    status: UserStatusEnum
    // true = conta esta com a senha padrao aplicada por um reset do super admin
    // e ainda nao foi trocada pelo dono.
    usingDefaultPassword?: boolean
}

export interface userInfoResponse {
    id: number;
    nome: string;
    email: string;
    login: string;
    role: string;
    status: string;
    criacao: Date,
    organizationUsers: organizationUsers[]
}

export interface UserResponse {
  id: number;
  nome: string;
  email: string;
  login: string;
  role: string;
  status: string;
  criacao: string;
  org_name: string;
  org_id: number;
}

interface UserState {
    userInfo: UserInfo,
    userList: Array<any>,
    clientList: Array<UserInfo>,
    depositors: Array<UserInfo>,
    modalStatus: boolean,
    wallets: Array<any> | null,
    deposits: Array<any>,
    quotes: Array<any>,
    blockedList: Array<any> | null,
    modalDocumentStatus: boolean,
    sendDocumentResponse: boolean,
    hardWallets: Array<any> | null,
    loading: {
        register: boolean
    }
}

export const resetUserInfoState: UserInfo = {
    id: 0,
    role: '',
    accessToken: '',
    organizationUsers: [{
        id: 0,
        fee: '',
        joinedAt: '',
        organization: {
            id: 0,
            organizationName: '',
            createdAt: '',
            updatedAt: '',
            isActive: false
        }
    }],
    document: {
        document: '',
        updatedAt: '',
        status: UserDocumentStatus.WAITING,
    },
    login: '',
    name: '',
    email: '',
    status: UserStatusEnum.INACTIVE
};

const initialState: UserState = {
  userInfo: resetUserInfoState,
  userList: [],
  clientList: [],
  depositors: [],
  modalStatus: false,
  wallets: [],
  deposits: [],
  quotes: [],
  blockedList: null,
  modalDocumentStatus: false,
  sendDocumentResponse: false,
  hardWallets: null,
  loading: {
    register: false
  }
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserInfo: (state, action: PayloadAction<UserInfo>) => {
            state.userInfo = action.payload;
        },
        setUsersList: (state, action: PayloadAction<Array<any>>) => {
            state.userList = action.payload;
        },
        setUserModalStatus: (state, action: PayloadAction<boolean>) => {
            state.modalStatus = action.payload;
        },
        setClientsList: (state, action: PayloadAction<Array<UserInfo>>) => {
            state.clientList = action.payload;
        },
        setDepositorsList: (state, action: PayloadAction<Array<UserInfo>>) => {
            state.depositors = action.payload;
        },
        setUserWallets: (state, action: PayloadAction<Array<any> | null>) => {
            state.wallets = action.payload;
        },
        setDepositsList: (state, action: PayloadAction<Array<any>>) => {
            state.deposits = action.payload;
        },
        setQuotes: (state, action: PayloadAction<Array<any>>) => {
            state.quotes = action.payload;
        },
        setModalDocument: (state, action: PayloadAction<boolean>) => {
            state.modalDocumentStatus = action.payload;
        },
        setSendDocumentStatus: (state, action: PayloadAction<boolean>) => {
            state.sendDocumentResponse = action.payload;
        },
        setHardWallets: (state, action: PayloadAction<Array<any>>) => {
            state.hardWallets = action.payload;
        },
        setRegisterStatus: (state, action: PayloadAction<boolean>) => {
            state.loading.register = action.payload;
        },
        setBlockedList: (state, action: PayloadAction<Array<any>>) => {
            state.blockedList = action.payload;
        },
    }
  });
  
export const {
    setUserInfo,
    setUsersList,
    setUserModalStatus,
    setClientsList,
    setDepositorsList,
    setUserWallets,
    setDepositsList,
    setQuotes,
    setModalDocument,
    setSendDocumentStatus,
    setHardWallets,
    setRegisterStatus,
    setBlockedList
} = userSlice.actions;

// customs func
export const getUserInfo            = (state: RootState): UserInfo => state.user.userInfo;
export const getUserList            = (state: RootState): Array<any> => state.user.userList;
export const getClientList          = (state: RootState): Array<any> => state.user.clientList;
export const getDepositorsList      = (state: RootState): Array<any> => state.user.depositors;
export const getUserModalStatus     = (state: RootState): boolean => state.user.modalStatus;
export const getUserWallets         = (state: RootState): Array<any> | null => state.user.wallets;

export const getAllUsers = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    
    const state                         = getState();
    const userInfo : UserInfo           = state.user.userInfo;
    let userResponse : UserResponse[]   = [];

    const normalizedRole = normalizeRole(userInfo.role);

    if (normalizedRole === 'super-admin') {
        userResponse = await UserService.getAllUsers();
    } else if (normalizedRole === 'owner') {
        userResponse = await UserService.getUserByOrganization(getOrgId(state));
    }
    if (!userResponse) throw new Error('Erro ao tentar pegar usuarios');

    dispatch(setUsersList(userResponse));
    dispatch(setUserModalStatus(false))
}

export const updateUserStatus = (userId: number, status: string): AppThunk<Promise<void>> => async (dispatch) => {
    try {
        await UserService.updateStatus(userId, status);
    } catch (e) {
        console.error(e);
    } finally {
        dispatch(getAllUsers());
    }
};


export const resetUserPassword = (userId: number): AppThunk<Promise<boolean>> => async (dispatch) => {
    try {
        const alvo: any = await UserService.resetPassword(userId);
        await dispatch(getAllUsers());
        dispatch(openToast({
            title:   'Senha resetada',
            message: `${alvo?.name ?? 'Usuario'} (${alvo?.login ?? userId}) agora usa a senha padrao. Ele precisa troca-la no proprio perfil.`,
            effect:  'success',
        }));
        return true;
    } catch (e: any) {
        console.error('resetUserPassword:', e?.message ?? e);
        dispatch(openToast({
            title:   'Falha ao resetar senha',
            message: e?.message ?? 'Nao foi possivel resetar a senha desse usuario.',
            effect:  'error',
        }));
        return false;
    }
};

export const addUser = (
  payload: UserAddInfo,
  register: string = 'dashboard'
): AppThunk<Promise<boolean>> => async (dispatch) => {

  dispatch(setRegisterStatus(true));
  try {
    if (!payload) throw new Error('Missing data');

    const userResponse : any = await UserService.addUser(payload, register);

    if (!userResponse) throw new Error('Error creating user');

    dispatch( getAllUsers());

    return true;
  } catch (e) {
    console.error(e);
    return false;
  } finally {
    dispatch(setRegisterStatus(false));
  }
};


export const fetchUser = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const atual = getState().user.userInfo;
    if (!atual?.id) return;

    try {
        const doServidor: UserInfo = await UserService.fecthUser(atual.id);
        if (!doServidor) return;

        const atualizado: UserInfo = { ...atual, ...doServidor, accessToken: atual.accessToken };

        saveSession(atualizado);
        dispatch(setUserInfo(atualizado));
    } catch (e) {
        console.error('fetchUser:', e);
    }
}


export const fetchClientsByOrganization = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState()
    const userResponse : any = await UserService.getUsersClients(getOrgId(state));
    if (!userResponse) throw new Error('Erro ao tentar pegar usuarios')
    dispatch(setClientsList(userResponse));
}

export const getDepositorsByOrganization = () : AppThunk<Promise<void>> => async (dispatch,getState) => {
    const state = getState();
    const depositorResponse : any = await UserService.getUsersDepositors(getOrgId(state));
    if (!depositorResponse) throw new Error('Erro ao tentar pegar depositantes')
    dispatch(setDepositorsList(depositorResponse));
}

export const fetchDepositsByUser = (id:number): AppThunk<Promise<void>> => async (dispatch) => {
    try {
        const depositsByUser : any = await DepositsService.getDepositsByUser(id);
        if (!depositsByUser) throw new Error()
        dispatch(setDepositsList(depositsByUser));
    } catch (e) {
        console.error(e);
    }
}

export const fetchUserWallets = (userId:number) : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    const walletsResponse : any = await UserService.getUserWallets(state.user.userInfo.id);
    dispatch(setUserWallets(walletsResponse));
} 

export const changeOrganization = (status:boolean, id:number) : AppThunk<Promise<void>> => async (dispatch, getState) => {
    /*
    if (!status) throw new Error('Erro ao editar organizacoe - status');

    const organizationResponse = await OrganizarionService.editOrganization({status,id})
    if (!organizationResponse) throw new Error('Erro ao editar organizacoe');

    dispatch(getOrganizations());
    */
}

export const fetchUserQuotes = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    const quotesResponse : any = await UserService.getUserQuotes(state.user.userInfo.id);
    if (!quotesResponse) throw new Error('Erro ao tentar pegar carteiras do usuario')
    dispatch(setQuotes(quotesResponse));
}

export const sendDocument = ( data: SendDocumentDto ) : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    try {
        const sendDocumentResponse : any = await UserService.sendDocument(state.user.userInfo.id, data);
        if (!sendDocumentResponse) throw new Error('Erro ao adicionar o deocumento ao usuario')
        if (sendDocumentResponse.filename) {
            dispatch(setSendDocumentStatus(true));
            dispatch(fetchUser())
        } else dispatch(setSendDocumentStatus(false));
    } catch (e) {
        console.error(e);
    }
}

export const fetchUserHardWallets = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    const hardWalletResponse : any = await WalletService.getHwByUserId(state.user.userInfo.id);
    if (!hardWalletResponse) throw new Error('Erro ao tentar pegar carteiras cadastradas do usuario')
    dispatch(setHardWallets(hardWalletResponse));
}

export const setUserToOrganization = (payload:any) : AppThunk<Promise<void>> => async (dispatch, getState) => {
    try {
        const addUserToOrganization : any = await UserService.addOrgToUser(payload);
        if (!addUserToOrganization) throw new Error('Erro ao adicionar a organizacao ao usuario');
        // O backend (findUserById) nao retorna accessToken — preserva o token atual
        // para nao perder a sessao ao atualizar o userInfo apos aceitar o convite.
        const accessToken = getState().user.userInfo.accessToken;
        dispatch( setUserInfoLocally({ ...addUserToOrganization, accessToken }))
    } catch (e: any) {
        // Ex.: 'Ja existe essa ligacao' quando o convite e reprocessado — nao quebra.
        console.error('setUserToOrganization:', e?.response?.data?.message ?? e?.message ?? e);
    }
}

export const setUserInfoLocally = (userInfo: UserInfo) : AppThunk<Promise<void>> => async (dispatch) => {
    saveSession(userInfo);
    dispatch( setLoggedStatus(true));
    dispatch( setLoggedType(userInfo.role));
    dispatch( setUserInfo(userInfo));
}

export default userSlice.reducer;

export const fetchBlockedList = () : AppThunk<Promise<void>> => async (dispatch, getState) => {
    
    try {
        const state = getState();
        const list : Array<any> = await UserService.getBlocked(getOrgId(state));
        if (!list) throw new Error(' Erro ao pegar a lsita dos bloquedos ');
        dispatch( setBlockedList(list))
    } catch (e) {
        console.error(e)
    }  finally {
        
    }
}